import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import {
  deleteField,
  doc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import { firestore } from '@/firebase'
import {
  availabilitySlotsSchema,
  clinicalAvailabilityDocIdForConsultType,
  clinicalAvailabilitySyncStateDocId,
  clinicalCustomerDraftEntrySchema,
  clinicalReservationsDocSchema,
  CLINICAL_CUSTOMER_DRAFT_SELECTS_DOC_ID,
  CLINICAL_CUSTOMER_DRAFT_SELECT_MAX_AGE_MS,
  CLINICAL_RESERVATIONS_DOC_ID,
  consultationConsultTypeSchema,
  hasOverlappingClinicalReservation,
  type ClinicalCustomerDraftEntry,
  type ClinicalReservationEntry,
  type ConsultationConsultType,
  type TellescopeAvailabilitySlots,
} from '@gh/common/models'
import {
  toUsStateCode,
  getUsTimeZoneForState,
  DEFAULT_US_CUSTOMER_TIMEZONE,
} from '@gh/common/utils'

import createLogger from '@/utils/logger'
import {
  logClinicalCalendarConsultantFilter,
  uniqueSortedIds,
} from '@/utils/logClinicalCalendarConsultantFilter'
import { tellescopeVendorSyncApi } from '@/components/cloudFunctions/tellescopeVendorSync'
import type { TellescopeRouteTypeMap } from 'tellescope-functions/client'
import { DateTime } from 'luxon'
import { useCurrentTime } from '@/composables/useCurrentTime'

const logger = createLogger('stores/clinicalTellescopeAvailability.ts')

/** Soft target: how often an open slot picker pings the server. */
const SLOT_PICKER_PING_INTERVAL_MS = 3 * 60_000
/** Visit loop: re-ping while another sync is in flight if the state doc never reports it. */
const IN_FLIGHT_REPING_MS = 10_000
const DEFAULT_RETRY_DELAY_MS = 2_000
const MIN_RETRY_DELAY_MS = 1_000
/** Bounded wait before the slot picker gives up on a fresh sync and shows the error + Retry. */
const SLOT_PICKER_MAX_WAIT_MS = 45_000
const SLOT_PICKER_MAX_FAILURES = 3
/** After a fresh/synced response, how long the slots doc snapshot may lag the response. */
const SLOTS_DOC_CATCH_UP_MS = 20_000
const SLOT_PICKER_SLOW_MESSAGE = 'Availability is taking longer than expected. Please try again.'
const SLOT_PICKER_UNAVAILABLE_MESSAGE = 'Availability is temporarily unavailable. Please try again.'
/** Fallback when no remembered ping exists (matches server default `freshForMs`). */
const DEFAULT_FRESH_FOR_MS = 150_000

type ClinicalAvailabilityLifecycleDelegate = {
  onDocumentVisibilityChange: () => void
  onWindowOnline: () => void
  onWindowOffline: () => void
}

let lifecycleDelegate: ClinicalAvailabilityLifecycleDelegate | null = null
let moduleLifecycleListenersAttached = false

function onModuleDocumentVisibilityChange() {
  lifecycleDelegate?.onDocumentVisibilityChange()
}

function onModuleWindowOnline() {
  lifecycleDelegate?.onWindowOnline()
}

function onModuleWindowOffline() {
  lifecycleDelegate?.onWindowOffline()
}

function attachModuleLifecycleListeners(delegate: ClinicalAvailabilityLifecycleDelegate) {
  lifecycleDelegate = delegate
  if (moduleLifecycleListenersAttached || typeof document === 'undefined') {
    return
  }
  document.addEventListener('visibilitychange', onModuleDocumentVisibilityChange)
  window.addEventListener('online', onModuleWindowOnline)
  window.addEventListener('offline', onModuleWindowOffline)
  moduleLifecycleListenersAttached = true
}

function detachModuleLifecycleListeners() {
  lifecycleDelegate = null
  if (!moduleLifecycleListenersAttached || typeof document === 'undefined') {
    return
  }
  document.removeEventListener('visibilitychange', onModuleDocumentVisibilityChange)
  window.removeEventListener('online', onModuleWindowOnline)
  window.removeEventListener('offline', onModuleWindowOffline)
  moduleLifecycleListenersAttached = false
}

export function clinicalAvailabilitySlotKey(args: {
  tellescopeUserId: string
  startTimeInMS: number
  durationInMinutes: number
}): string {
  return `${args.tellescopeUserId}:${args.startTimeInMS}:${args.durationInMinutes}`
}

function parseClinicalCustomerDraftSelectsFromDoc(
  raw: unknown,
): Record<string, ClinicalCustomerDraftEntry> {
  if (!raw || typeof raw !== 'object') {
    return {}
  }
  const customerDrafts = (raw as { customerDrafts?: unknown }).customerDrafts
  if (!customerDrafts || typeof customerDrafts !== 'object') {
    return {}
  }
  const parsed: Record<string, ClinicalCustomerDraftEntry> = {}
  let malformedCount = 0
  for (const [customerId, entry] of Object.entries(customerDrafts as Record<string, unknown>)) {
    const result = clinicalCustomerDraftEntrySchema.safeParse(entry)
    if (result.success) {
      parsed[customerId] = result.data
    } else {
      malformedCount += 1
    }
  }
  if (malformedCount > 0) {
    logger.warn('Dropped malformed clinical customer draft select entries', {
      malformedCount,
    })
  }
  return parsed
}

/** Server response of `ensureClinicalAvailabilityFresh`; `lastSuccessAt` equals the slots doc `updatedAt` of that sync. */
export type ClinicalAvailabilityFreshness =
  TellescopeRouteTypeMap['ensureClinicalAvailabilityFresh']['response']

type RememberedPing = {
  response: ClinicalAvailabilityFreshness
  receivedAtMonotonicMs: number
  receivedAtWallMs: number
  /** Monotonic deadline derived from `retryAfterMs` (a duration, never a server timestamp). */
  retryNotBeforeMonotonicMs: number
  /** Wall-clock deadline derived from `retryAfterMs`. */
  retryNotBeforeWallMs: number
}

/** Parsed, TTL-valid draft pick for a customer (booking store adopts the latest pick). */
export type ClinicalCustomerDraftSelectView = {
  tellescopeUserId: string
  startTimeInMS: number
  durationInMinutes: number
  selectedAt: Date
}

function timestampToMillis(raw: unknown): number | null {
  if (raw == null) return null
  let date: Date
  if (raw instanceof Date) {
    date = raw
  } else if (typeof raw === 'object' && 'toDate' in raw && typeof raw.toDate === 'function') {
    date = (raw as { toDate: () => Date }).toDate()
  } else {
    date = new Date(raw as string | number)
  }
  const ms = date.getTime()
  return Number.isNaN(ms) ? null : ms
}

function isoToMillis(iso: string, field: string): number {
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) {
    throw new Error(`Invalid ${field} in ensureClinicalAvailabilityFresh response`)
  }
  return ms
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isFirestoreNotFound(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('code' in error)) {
    return false
  }
  const code = String((error as { code: unknown }).code)
  return code.includes('not-found')
}

export type ClinicalDraftSelectArgs = {
  customerId: string
  startTimeInMS: number
  endTimeInMS: number
  tellescopeUserId: string
}

export type ClinicalPingOptions = {
  consultType?: ConsultationConsultType
  /** Skip the local `retryAfterMs` gate (explicit Retry). */
  force?: boolean
}

/**
 * Pinia store for per-consultType clinical availability cache docs
 * (`availability_slots_clinical_main` / `availability_slots_clinical_followup`).
 *
 * The server owns sync scheduling: the client only pings `ensureClinicalAvailabilityFresh` when it
 * needs slots (session open, slot picker, consult type change, Retry) and reads the resulting
 * docs. Patient state is a client-side filter (same pattern as coach store).
 */
export const useClinicalTellescopeAvailabilityStore = defineStore(
  'clinicalTellescopeAvailability',
  () => {
    let unsubscribeTellescopeAvailabilitySnapshot: (() => void) | null = null
    let unsubscribeSyncStateSnapshot: (() => void) | null = null
    let unsubscribeReservationsSnapshot: (() => void) | null = null
    let unsubscribeDraftSelectsSnapshot: (() => void) | null = null

    /** Active consult type — selects which availability doc to subscribe/sync. */
    const consultType = ref<ConsultationConsultType>('clinical_main')
    /** Optional UI filter — patient state of residence (does not change the subscribed doc). */
    const filterStateCode = ref<string | null>(null)
    const tellescopeAvailability = ref<TellescopeAvailabilitySlots | null>(null)
    const clinicalReservations = ref<Record<string, ClinicalReservationEntry>>({})
    const clinicalCustomerDraftSelects = ref<Record<string, ClinicalCustomerDraftEntry>>({})
    /** Slots hidden for this picker session after a server `already-exists` reserve rejection. */
    const sessionHiddenSlotKeys = ref<Set<string>>(new Set())
    const externalUserLicensedStateCodes = ref<Record<string, string[]>>({})
    const externalUserIdToConsultantId = ref<Record<string, string>>({})
    const isSubscribed = ref(false)
    let subscribeInFlight = false
    let subscribedDocId: string | null = null

    /** `lastSuccessAt` of the active consult type's sync state doc (server clock). */
    const syncStateLastSuccessAtMs = ref<number | null>(null)
    /** Slots doc `updatedAt` the open slot picker waits for; null until the server vouches for a sync. */
    const requiredSuccessAtMs = ref<number | null>(null)
    /** Bumped to cancel an in-progress slot-picker visit. */
    const visitEpoch = ref(0)
    let periodicPingTimer: ReturnType<typeof setInterval> | null = null
    let slotPickerVisitActive = false
    let documentHiddenAtWallMs: number | null = null
    let offlineAtWallMs: number | null = null
    /** Server `usableUntilMs` while the picker runs on a stale-but-allowed cache (R-11). */
    let activeStaleUsableUntilMs: number | null = null
    let activeStaleUsableServerNowMs: number | null = null
    let activeStaleUsableReceivedAtMonotonicMs: number | null = null
    let activeStaleUsableReceivedAtWallMs: number | null = null
    const slotPickerVisitError = ref<string | null>(null)
    const rememberedPings = new Map<ConsultationConsultType, RememberedPing>()
    const pingsInFlight = new Map<ConsultationConsultType, Promise<ClinicalAvailabilityFreshness>>()

    const { currentTime: everyFiveMinutes } = useCurrentTime({ durationInMinutes: 5 })
    const { currentTime: reservationExpiryTick } = useCurrentTime({ durationInMinutes: 1 })

    const documentReferenceClinicalCustomerDraftSelects = doc(
      firestore,
      'tellescope_availability_slots',
      CLINICAL_CUSTOMER_DRAFT_SELECTS_DOC_ID,
    )

    const activeDraftSelect = ref<ClinicalDraftSelectArgs | null>(null)

    function isDocumentHidden(): boolean {
      return typeof document !== 'undefined' && document.hidden
    }

    function freshnessWindowMsForType(type: ConsultationConsultType): number {
      const remembered = rememberedPings.get(type)
      return remembered?.response.freshForMs ?? DEFAULT_FRESH_FOR_MS
    }

    function maxFreshnessWindowMs(): number {
      let max = DEFAULT_FRESH_FOR_MS
      for (const type of rememberedPings.keys()) {
        max = Math.max(max, freshnessWindowMsForType(type))
      }
      return max
    }

    function dropRememberedPingsIfStaleAfterHiddenOrOffline(hiddenDurationMs: number) {
      if (hiddenDurationMs >= maxFreshnessWindowMs()) {
        rememberedPings.clear()
      }
    }

    function onDocumentVisibilityChange() {
      if (isDocumentHidden()) {
        documentHiddenAtWallMs = Date.now()
        return
      }
      const hiddenAt = documentHiddenAtWallMs
      documentHiddenAtWallMs = null
      const hiddenDurationMs = hiddenAt == null ? 0 : Math.max(0, Date.now() - hiddenAt)
      dropRememberedPingsIfStaleAfterHiddenOrOffline(hiddenDurationMs)
      if (slotPickerVisitActive) {
        pingFreshness()
          .then((response) => {
            handleSlotPickerPingResult(response)
          })
          .catch((err) => {
            logger.warn('Clinical availability ping on tab visible failed', { err })
            evaluateActiveStaleUsableExpiry()
          })
      }
    }

    function onWindowOffline() {
      offlineAtWallMs = Date.now()
    }

    function onWindowOnline() {
      const offlineDuration =
        offlineAtWallMs == null ? 0 : Math.max(0, Date.now() - offlineAtWallMs)
      offlineAtWallMs = null
      dropRememberedPingsIfStaleAfterHiddenOrOffline(offlineDuration)
      if (slotPickerVisitActive) {
        pingFreshness()
          .then((response) => {
            handleSlotPickerPingResult(response)
          })
          .catch((err) => {
            logger.warn('Clinical availability ping on online failed', { err })
            evaluateActiveStaleUsableExpiry()
          })
      }
    }

    function attachLifecycleListeners() {
      attachModuleLifecycleListeners({
        onDocumentVisibilityChange,
        onWindowOnline,
        onWindowOffline,
      })
    }

    function detachLifecycleListeners() {
      documentHiddenAtWallMs = null
      offlineAtWallMs = null
      detachModuleLifecycleListeners()
    }

    function maybeDetachLifecycleListeners() {
      if (isSubscribed.value || slotPickerVisitActive) {
        return
      }
      detachLifecycleListeners()
    }

    function localDraftSelectMatchesEntry(
      local: ClinicalDraftSelectArgs,
      entry: ClinicalCustomerDraftEntry,
    ): boolean {
      return (
        entry.tellescopeUserId === local.tellescopeUserId &&
        entry.startTimeInMS === local.startTimeInMS &&
        entry.endTimeInMS === local.endTimeInMS
      )
    }

    function handleAvailabilitySlotsListenerError(error: unknown, docId: string) {
      logger.error('Error in onSnapshot for clinical availability slots', { error, docId })
      if (unsubscribeTellescopeAvailabilitySnapshot) {
        unsubscribeTellescopeAvailabilitySnapshot()
        unsubscribeTellescopeAvailabilitySnapshot = null
      }
      subscribedDocId = null
      isSubscribed.value = false
      if (slotPickerVisitActive) {
        slotPickerVisitError.value = SLOT_PICKER_UNAVAILABLE_MESSAGE
      }
    }

    function handleSyncStateListenerError(error: unknown, docId: string) {
      logger.error('Error in onSnapshot for clinical availability sync state', { error, docId })
      if (unsubscribeSyncStateSnapshot) {
        unsubscribeSyncStateSnapshot()
        unsubscribeSyncStateSnapshot = null
      }
      subscribedDocId = null
      isSubscribed.value = false
    }

    function handleReservationsListenerError(error: unknown) {
      logger.error('Error in onSnapshot for clinical reservations', { error })
      if (unsubscribeReservationsSnapshot) {
        unsubscribeReservationsSnapshot()
        unsubscribeReservationsSnapshot = null
      }
      isSubscribed.value = false
    }

    function handleDraftSelectsListenerError(error: unknown) {
      logger.error('Error in onSnapshot for clinical customer draft selects', { error })
      if (unsubscribeDraftSelectsSnapshot) {
        unsubscribeDraftSelectsSnapshot()
        unsubscribeDraftSelectsSnapshot = null
      }
      isSubscribed.value = false
    }

    function unsubscribeAvailabilityOnly() {
      if (unsubscribeTellescopeAvailabilitySnapshot) {
        unsubscribeTellescopeAvailabilitySnapshot()
        unsubscribeTellescopeAvailabilitySnapshot = null
      }
      if (unsubscribeSyncStateSnapshot) {
        unsubscribeSyncStateSnapshot()
        unsubscribeSyncStateSnapshot = null
      }
      subscribedDocId = null
    }

    function unsubscribeSnapshot() {
      unsubscribeAvailabilityOnly()
      if (unsubscribeReservationsSnapshot) {
        unsubscribeReservationsSnapshot()
        unsubscribeReservationsSnapshot = null
      }
      if (unsubscribeDraftSelectsSnapshot) {
        unsubscribeDraftSelectsSnapshot()
        unsubscribeDraftSelectsSnapshot = null
      }
      isSubscribed.value = false
    }

    function setConsultType(next: ConsultationConsultType) {
      const parsed = consultationConsultTypeSchema.parse(next)
      if (parsed !== consultType.value) {
        // updatedAt is derived from tellescopeAvailability; null clears calendar readiness.
        tellescopeAvailability.value = null
        syncStateLastSuccessAtMs.value = null
        requiredSuccessAtMs.value = null
        visitEpoch.value++
      }
      consultType.value = parsed
    }

    function setFilterStateCode(stateInput: string | null) {
      if (!stateInput) {
        filterStateCode.value = null
        return
      }
      const next = toUsStateCode(stateInput)
      if (!next) {
        throw new Error(`Invalid US state for clinical availability filter: ${stateInput}`)
      }
      filterStateCode.value = next
    }

    function setLicensedStateMaps(args: {
      userLicensedStateCodes: Record<string, string[]>
      userIdToConsultantId: Record<string, string>
    }) {
      externalUserLicensedStateCodes.value = args.userLicensedStateCodes
      externalUserIdToConsultantId.value = args.userIdToConsultantId
    }

    /** @deprecated Use setFilterStateCode. */
    function setStateCode(stateInput: string) {
      setFilterStateCode(stateInput)
    }

    function subscribeAvailabilityDoc() {
      const docId = clinicalAvailabilityDocIdForConsultType(consultType.value)
      if (subscribedDocId === docId && unsubscribeTellescopeAvailabilitySnapshot) {
        return
      }

      unsubscribeAvailabilityOnly()
      tellescopeAvailability.value = null
      syncStateLastSuccessAtMs.value = null

      const slotsRef = doc(firestore, 'tellescope_availability_slots', docId)
      const syncStateRef = doc(
        firestore,
        'tellescope_availability_slots',
        clinicalAvailabilitySyncStateDocId(consultType.value),
      )

      const unsubscribe = onSnapshot(
        slotsRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const { success, data, error } = availabilitySlotsSchema.safeParse({
              id: docSnap.id,
              ...docSnap.data(),
            })
            if (!success) {
              logger.error('Error parsing clinical tellescopeAvailability data', {
                data,
                error,
                snap: docSnap.data(),
              })
              return
            }
            tellescopeAvailability.value = data
            logger.info('clinical tellescopeAvailability updated via onSnapshot', {
              consultType: consultType.value,
              docId,
              availabilityBlocksCount: data.availabilityBlocks?.length || 0,
              updatedAt: data.updatedAt,
            })
          } else {
            logger.warn('clinical availability document not found', { docId })
            tellescopeAvailability.value = null
          }
        },
        (error) => {
          handleAvailabilitySlotsListenerError(error, docId)
        },
      )

      const unsubscribeSyncState = onSnapshot(
        syncStateRef,
        (docSnap) => {
          syncStateLastSuccessAtMs.value = docSnap.exists()
            ? timestampToMillis(docSnap.data()?.lastSuccessAt)
            : null
        },
        (error) => {
          handleSyncStateListenerError(error, clinicalAvailabilitySyncStateDocId(consultType.value))
        },
      )

      unsubscribeTellescopeAvailabilitySnapshot = unsubscribe
      unsubscribeSyncStateSnapshot = unsubscribeSyncState
      subscribedDocId = docId
    }

    async function subscribe() {
      if (subscribeInFlight) {
        return
      }
      subscribeInFlight = true
      logger.info('subscribe clinical availability + reservations', {
        consultType: consultType.value,
      })
      try {
        if (!isSubscribed.value) {
          unsubscribeSnapshot()
        }

        subscribeAvailabilityDoc()

        if (!unsubscribeReservationsSnapshot) {
          const reservationsRef = doc(
            firestore,
            'tellescope_availability_slots',
            CLINICAL_RESERVATIONS_DOC_ID,
          )
          unsubscribeReservationsSnapshot = onSnapshot(
            reservationsRef,
            (docSnap) => {
              if (!docSnap.exists()) {
                clinicalReservations.value = {}
                return
              }
              const parsed = clinicalReservationsDocSchema.safeParse(docSnap.data())
              if (!parsed.success) {
                logger.error('Error parsing clinical reservations', {
                  error: parsed.error,
                })
                return
              }
              clinicalReservations.value = parsed.data.reservations
            },
            (error) => {
              handleReservationsListenerError(error)
            },
          )
        }

        if (!unsubscribeDraftSelectsSnapshot) {
          unsubscribeDraftSelectsSnapshot = onSnapshot(
            documentReferenceClinicalCustomerDraftSelects,
            (docSnap) => {
              if (!docSnap.exists()) {
                clinicalCustomerDraftSelects.value = {}
                return
              }
              clinicalCustomerDraftSelects.value = parseClinicalCustomerDraftSelectsFromDoc(
                docSnap.data({ serverTimestamps: 'estimate' }),
              )
            },
            (error) => {
              handleDraftSelectsListenerError(error)
            },
          )
        }

        isSubscribed.value = true
        attachLifecycleListeners()
      } finally {
        subscribeInFlight = false
      }
    }

    /** Drop dead Firestore listeners and re-attach (Retry / post-auth recovery). */
    async function forceResubscribe() {
      unsubscribeSnapshot()
      await subscribe()
    }

    async function makeSlotAsSelected(args: ClinicalDraftSelectArgs) {
      logger.info('makeSlotAsSelected called', { customerId: args.customerId })
      activeDraftSelect.value = args
      try {
        const draftData = {
          selectedAt: serverTimestamp(),
          startTimeInMS: args.startTimeInMS,
          endTimeInMS: args.endTimeInMS,
          tellescopeUserId: args.tellescopeUserId,
        }

        await runTransaction(firestore, async (transaction) => {
          const docRef = documentReferenceClinicalCustomerDraftSelects
          const docSnap = await transaction.get(docRef)
          if (!docSnap.exists()) {
            transaction.set(docRef, {
              customerDrafts: { [args.customerId]: draftData },
              createdAt: serverTimestamp(),
            })
          } else {
            transaction.update(docRef, {
              [`customerDrafts.${args.customerId}`]: draftData,
            })
          }
        })
        if (activeDraftSelect.value === null) {
          await unmarkSlotAsSelected(args.customerId).catch(() => undefined)
          return
        }
        if (activeDraftSelect.value !== args) {
          return
        }
        logger.info('Clinical slot marked as selected in Firestore', {
          customerId: args.customerId,
        })
      } catch (error) {
        if (activeDraftSelect.value === args) {
          activeDraftSelect.value = null
        }
        logger.error('Error marking clinical slot as selected', {
          customerId: args.customerId,
          error,
        })
        throw error
      }
    }

    async function unmarkSlotAsSelected(customerId: string) {
      if (!customerId) {
        logger.error('unmarkSlotAsSelected called but customerId is empty')
        return
      }
      logger.info('unmarkSlotAsSelected called', { customerId })
      try {
        await updateDoc(documentReferenceClinicalCustomerDraftSelects, {
          [`customerDrafts.${customerId}`]: deleteField(),
        })
        if (activeDraftSelect.value?.customerId === customerId) {
          activeDraftSelect.value = null
        }
        logger.info('Clinical slot unmarked in Firestore', { customerId })
      } catch (error) {
        if (isFirestoreNotFound(error)) {
          return
        }
        logger.error('Error unmarking clinical slot', { customerId, error })
        throw error
      }
    }

    function clearSessionHiddenSlots() {
      sessionHiddenSlotKeys.value = new Set()
    }

    function hideSlotForSession(args: {
      tellescopeUserId: string
      startTimeInMS: number
      durationInMinutes: number
    }) {
      const key = clinicalAvailabilitySlotKey(args)
      if (sessionHiddenSlotKeys.value.has(key)) {
        return
      }
      const next = new Set(sessionHiddenSlotKeys.value)
      next.add(key)
      sessionHiddenSlotKeys.value = next
    }

    function pruneSessionHiddenSlots() {
      if (sessionHiddenSlotKeys.value.size === 0) {
        return
      }
      const blocks = tellescopeAvailability.value?.availabilityBlocks
      if (!blocks) {
        return
      }
      const present = new Set(
        blocks.map((block) =>
          clinicalAvailabilitySlotKey({
            tellescopeUserId: block.userId,
            startTimeInMS: block.startTimeInMS,
            durationInMinutes: block.durationInMinutes,
          }),
        ),
      )
      const next = new Set([...sessionHiddenSlotKeys.value].filter((key) => present.has(key)))
      if (next.size !== sessionHiddenSlotKeys.value.size) {
        sessionHiddenSlotKeys.value = next
      }
    }

    function signOut() {
      endSlotPickerVisit()
      unsubscribeSnapshot()
      detachLifecycleListeners()
      consultType.value = 'clinical_main'
      filterStateCode.value = null
      tellescopeAvailability.value = null
      clinicalReservations.value = {}
      clinicalCustomerDraftSelects.value = {}
      clearSessionHiddenSlots()
      activeDraftSelect.value = null
      externalUserLicensedStateCodes.value = {}
      externalUserIdToConsultantId.value = {}
      syncStateLastSuccessAtMs.value = null
      requiredSuccessAtMs.value = null
      rememberedPings.clear()
      pingsInFlight.clear()
      subscribeInFlight = false
      subscribedDocId = null
    }

    const filterTimezone = computed(() => {
      if (!filterStateCode.value) {
        return DEFAULT_US_CUSTOMER_TIMEZONE
      }
      return getUsTimeZoneForState(filterStateCode.value) ?? DEFAULT_US_CUSTOMER_TIMEZONE
    })

    const availabilitySlots = computed(() => {
      const state = filterStateCode.value
      const licensedMap = externalUserLicensedStateCodes.value
      void reservationExpiryTick.value
      const now = new Date()
      const zone = filterTimezone.value
      const blocks = tellescopeAvailability.value?.availabilityBlocks
      if (!blocks) return []

      return blocks
        .filter((slot) => {
          if (!state) return true
          const licensed = licensedMap[slot.userId]
          if (!licensed?.length) return false
          return licensed.includes(state)
        })
        .filter(
          (slot) =>
            !sessionHiddenSlotKeys.value.has(
              clinicalAvailabilitySlotKey({
                tellescopeUserId: slot.userId,
                startTimeInMS: slot.startTimeInMS,
                durationInMinutes: slot.durationInMinutes,
              }),
            ),
        )
        .filter((slot) => {
          return !hasOverlappingClinicalReservation({
            reservations: clinicalReservations.value,
            tellescopeUserId: slot.userId,
            startTimeInMS: slot.startTimeInMS,
            durationInMinutes: slot.durationInMinutes,
            now,
          })
        })
        .map((slot: TellescopeAvailabilitySlots['availabilityBlocks'][number]) => ({
          ...slot,
          startDate: DateTime.fromMillis(slot.startTimeInMS, { zone }).startOf('day').toJSDate(),
          startDateString: DateTime.fromMillis(slot.startTimeInMS, { zone }).toFormat('yyyy-MM-dd'),
        }))
    })

    watch(
      [
        () => tellescopeAvailability.value?.availabilityBlocks,
        filterStateCode,
        externalUserLicensedStateCodes,
        externalUserIdToConsultantId,
        availabilitySlots,
      ],
      ([blocks, visitState, licensedMap, consultantMap, filteredSlots]) => {
        const cacheUserIds = uniqueSortedIds((blocks ?? []).map((block) => block.userId))
        const kept: Array<{
          tellescopeUserId: string
          consultantId?: string
          licensedStates: string[]
        }> = []
        const dropped: Array<{
          tellescopeUserId: string
          consultantId?: string
          licensedStates: string[]
          reason: 'no-licensed-states' | 'visit-state-not-licensed'
        }> = []

        for (const tellescopeUserId of cacheUserIds) {
          const licensedStates = licensedMap[tellescopeUserId] ?? []
          const consultantId = consultantMap[tellescopeUserId]
          if (!visitState) {
            kept.push({ tellescopeUserId, consultantId, licensedStates })
            continue
          }
          if (!licensedStates.length) {
            dropped.push({
              tellescopeUserId,
              consultantId,
              licensedStates,
              reason: 'no-licensed-states',
            })
            continue
          }
          if (!licensedStates.includes(visitState)) {
            dropped.push({
              tellescopeUserId,
              consultantId,
              licensedStates,
              reason: 'visit-state-not-licensed',
            })
            continue
          }
          kept.push({ tellescopeUserId, consultantId, licensedStates })
        }

        const afterReservationUserIds = uniqueSortedIds(filteredSlots.map((slot) => slot.userId))
        const droppedByReservation = kept
          .map((row) => row.tellescopeUserId)
          .filter((userId) => !afterReservationUserIds.includes(userId))

        logClinicalCalendarConsultantFilter('licensed-states', {
          visitState,
          rawBlockCount: blocks?.length ?? 0,
          uniqueConsultantsInCache: cacheUserIds.length,
          keptCount: kept.length,
          droppedCount: dropped.length,
          kept,
          dropped,
          uniqueAfterReservationFilter: afterReservationUserIds.length,
          droppedFullyByReservation: droppedByReservation,
        })
      },
    )

    const updatedAt = computed(() => {
      const ms = timestampToMillis(tellescopeAvailability.value?.updatedAt)
      return ms == null ? null : new Date(ms)
    })

    /** True once the slots doc snapshot reflects the sync the server vouched for this visit. */
    const isSlotsFreshForVisit = computed(() => {
      const required = requiredSuccessAtMs.value
      const slotsUpdatedAt = updatedAt.value
      return required != null && slotsUpdatedAt != null && slotsUpdatedAt.getTime() >= required
    })

    // Local durations and the server offset use the monotonic clock so a wrong device clock (or a
    // mid-session clock change) never affects readiness; server timestamps are only ever compared
    // with other server timestamps or with `serverNow + elapsed`.
    const monotonicNow = () => performance.now()

    function rememberedElapsedMs(remembered: RememberedPing): number {
      const monotonicElapsed = monotonicNow() - remembered.receivedAtMonotonicMs
      const wallElapsed = Math.max(0, Date.now() - remembered.receivedAtWallMs)
      return Math.max(monotonicElapsed, wallElapsed)
    }

    function isRememberedRetryGateOpen(remembered: RememberedPing): boolean {
      return (
        monotonicNow() < remembered.retryNotBeforeMonotonicMs &&
        Date.now() < remembered.retryNotBeforeWallMs
      )
    }

    function pingReceiptElapsedMs(receivedAtMonotonicMs: number, receivedAtWallMs: number): number {
      const monotonicElapsed = monotonicNow() - receivedAtMonotonicMs
      const wallElapsed = Math.max(0, Date.now() - receivedAtWallMs)
      return Math.max(monotonicElapsed, wallElapsed)
    }

    function estimatedServerNowMsForPing(
      response: ClinicalAvailabilityFreshness,
      type: ConsultationConsultType,
    ): number {
      const remembered = rememberedPings.get(type)
      if (remembered && remembered.response === response) {
        return response.serverNowMs + rememberedElapsedMs(remembered)
      }
      return response.serverNowMs
    }

    function clearActiveStaleUsableVisit() {
      activeStaleUsableUntilMs = null
      activeStaleUsableServerNowMs = null
      activeStaleUsableReceivedAtMonotonicMs = null
      activeStaleUsableReceivedAtWallMs = null
    }

    function evaluateActiveStaleUsableExpiry() {
      if (
        !slotPickerVisitActive ||
        activeStaleUsableUntilMs == null ||
        activeStaleUsableServerNowMs == null ||
        activeStaleUsableReceivedAtMonotonicMs == null ||
        activeStaleUsableReceivedAtWallMs == null
      ) {
        return
      }
      const elapsed = pingReceiptElapsedMs(
        activeStaleUsableReceivedAtMonotonicMs,
        activeStaleUsableReceivedAtWallMs,
      )
      if (activeStaleUsableServerNowMs + elapsed >= activeStaleUsableUntilMs) {
        slotPickerVisitError.value = SLOT_PICKER_UNAVAILABLE_MESSAGE
        clearActiveStaleUsableVisit()
      }
    }

    function handleSlotPickerPingResult(response: ClinicalAvailabilityFreshness) {
      if (response.status === 'fresh' || response.status === 'synced') {
        clearActiveStaleUsableVisit()
        slotPickerVisitError.value = null
      } else {
        evaluateActiveStaleUsableExpiry()
      }
    }

    function clearStaleUsableWhenSlotsDocAdvances(slotsUpdatedAtMs: number) {
      const acceptedStaleSuccessAtMs = requiredSuccessAtMs.value
      if (
        !slotPickerVisitActive ||
        acceptedStaleSuccessAtMs == null ||
        slotsUpdatedAtMs <= acceptedStaleSuccessAtMs
      ) {
        return
      }
      if (activeStaleUsableUntilMs != null || slotPickerVisitError.value != null) {
        clearActiveStaleUsableVisit()
        if (slotPickerVisitError.value === SLOT_PICKER_UNAVAILABLE_MESSAGE) {
          slotPickerVisitError.value = null
        }
      }
    }

    function estimatedServerNowMsForDraftSelectTtl(): number {
      for (const [type, remembered] of rememberedPings) {
        return estimatedServerNowMsForPing(remembered.response, type)
      }
      return Date.now()
    }

    function getCustomerDraftSelect(customerId: string): ClinicalCustomerDraftSelectView | null {
      if (!customerId) return null
      const entry = clinicalCustomerDraftSelects.value[customerId]
      if (!entry) return null
      const selectedAtMs = entry.selectedAt.getTime()
      if (Number.isNaN(selectedAtMs)) return null
      const cutoff =
        estimatedServerNowMsForDraftSelectTtl() - CLINICAL_CUSTOMER_DRAFT_SELECT_MAX_AGE_MS
      if (selectedAtMs < cutoff) return null
      const durationMs = entry.endTimeInMS - entry.startTimeInMS
      if (durationMs <= 0) return null
      return {
        tellescopeUserId: entry.tellescopeUserId,
        startTimeInMS: entry.startTimeInMS,
        durationInMinutes: durationMs / 60_000,
        selectedAt: entry.selectedAt,
      }
    }

    function isStaleUsableStatus(status: ClinicalAvailabilityFreshness['status']): boolean {
      return status === 'failed' || status === 'backoff' || status === 'in_flight'
    }

    /**
     * Accept picker readiness on the last good cache when the server vouches via `usableUntilMs`.
     * Does not relax slot availability — only visit readiness.
     */
    function tryAcceptStaleUsableCache(
      response: ClinicalAvailabilityFreshness,
      type: ConsultationConsultType,
    ): boolean {
      if (
        !isStaleUsableStatus(response.status) ||
        response.usableUntilMs == null ||
        !response.lastSuccessAt
      ) {
        return false
      }
      const estimatedServerNow = estimatedServerNowMsForPing(response, type)
      if (estimatedServerNow >= response.usableUntilMs) {
        return false
      }
      const lastSuccessAtMs = isoToMillis(response.lastSuccessAt, 'lastSuccessAt')
      const slotsUpdatedAtMs = updatedAt.value?.getTime()
      if (slotsUpdatedAtMs == null || slotsUpdatedAtMs < lastSuccessAtMs) {
        return false
      }

      requiredSuccessAtMs.value = lastSuccessAtMs
      const remembered = rememberedPings.get(type)
      activeStaleUsableUntilMs = response.usableUntilMs
      activeStaleUsableServerNowMs = response.serverNowMs
      activeStaleUsableReceivedAtMonotonicMs = remembered?.receivedAtMonotonicMs ?? monotonicNow()
      activeStaleUsableReceivedAtWallMs = remembered?.receivedAtWallMs ?? Date.now()
      slotPickerVisitError.value = null
      return true
    }

    /**
     * Asks the server to make sure the slots doc is fresh. The server decides whether a sync is due
     * and runs at most one per window for everyone. Concurrent pings per consult type share one
     * call, and a remembered `retryAfterMs` is honored unless `force` is set.
     */
    function pingFreshness(options?: ClinicalPingOptions): Promise<ClinicalAvailabilityFreshness> {
      const type = options?.consultType ?? consultType.value
      const inFlight = pingsInFlight.get(type)
      if (inFlight) return inFlight

      const remembered = rememberedPings.get(type)
      if (!options?.force && remembered && isRememberedRetryGateOpen(remembered)) {
        return Promise.resolve(remembered.response)
      }

      const request = (async () => {
        const response: ClinicalAvailabilityFreshness =
          await tellescopeVendorSyncApi.ensureClinicalAvailabilityFresh({ consultType: type })
        const receivedAtMonotonicMs = monotonicNow()
        const receivedAtWallMs = Date.now()
        const retryAfterMs = response.retryAfterMs ?? 0
        rememberedPings.set(type, {
          response,
          receivedAtMonotonicMs,
          receivedAtWallMs,
          retryNotBeforeMonotonicMs: receivedAtMonotonicMs + retryAfterMs,
          retryNotBeforeWallMs: receivedAtWallMs + retryAfterMs,
        })
        return response
      })().finally(() => {
        pingsInFlight.delete(type)
      })
      pingsInFlight.set(type, request)
      return request
    }

    /** A remembered fresh/synced response the server would still call fresh right now. */
    function rememberedFreshResponse(
      type: ConsultationConsultType,
    ): ClinicalAvailabilityFreshness | null {
      const remembered = rememberedPings.get(type)
      if (!remembered) return null
      const { response } = remembered
      if (
        (response.status !== 'fresh' && response.status !== 'synced') ||
        !response.lastSuccessAt
      ) {
        return null
      }
      const estimatedServerNow = response.serverNowMs + rememberedElapsedMs(remembered)
      const dataAgeMs = estimatedServerNow - isoToMillis(response.lastSuccessAt, 'lastSuccessAt')
      return dataAgeMs < response.freshForMs ? response : null
    }

    type PingFreshnessWithinVisitBudgetResult =
      | { kind: 'response'; response: ClinicalAvailabilityFreshness }
      | { kind: 'error' }
      | { kind: 'timeout' }

    /** Caps a single ping at the visit budget without cancelling the shared in-flight promise. */
    async function pingFreshnessWithinVisitBudget(
      startedAt: number,
      type: ConsultationConsultType,
      force: boolean,
    ): Promise<PingFreshnessWithinVisitBudgetResult> {
      const budgetMs = SLOT_PICKER_MAX_WAIT_MS - (monotonicNow() - startedAt)
      if (budgetMs <= 0) {
        return { kind: 'timeout' }
      }
      let timeoutId: ReturnType<typeof setTimeout> | undefined
      try {
        const raced = await Promise.race([
          pingFreshness({ consultType: type, force }).then(
            (response) => ({ kind: 'response' as const, response }),
            (err) => ({ kind: 'error' as const, err }),
          ),
          new Promise<{ kind: 'timeout' }>((resolve) => {
            timeoutId = setTimeout(() => resolve({ kind: 'timeout' }), budgetMs)
          }),
        ])
        if (raced.kind === 'timeout') {
          return { kind: 'timeout' }
        }
        if (raced.kind === 'error') {
          logger.warn('Clinical availability ping failed', { err: raced.err, consultType: type })
          return { kind: 'error' }
        }
        return { kind: 'response', response: raced.response }
      } finally {
        if (timeoutId != null) {
          clearTimeout(timeoutId)
        }
      }
    }

    /** Resolves true when `predicate` holds, false on timeout or when the visit was cancelled. */
    function waitFor(predicate: () => boolean, timeoutMs: number, epoch: number): Promise<boolean> {
      const isDone = () => visitEpoch.value !== epoch || predicate()
      return new Promise((resolve) => {
        if (isDone()) {
          resolve(visitEpoch.value === epoch)
          return
        }
        const timer = setTimeout(() => finish(false), timeoutMs)
        const stop = watch(isDone, (done) => {
          if (done) finish(visitEpoch.value === epoch)
        })
        function finish(result: boolean) {
          clearTimeout(timer)
          stop()
          resolve(result)
        }
      })
    }

    function stopPeriodicPings() {
      if (periodicPingTimer) {
        clearInterval(periodicPingTimer)
        periodicPingTimer = null
      }
    }

    function startPeriodicPings() {
      stopPeriodicPings()
      // Results only update the slots doc in place; a failed or busy periodic ping never touches
      // readiness because slots are trusted until the next success.
      periodicPingTimer = setInterval(() => {
        if (isDocumentHidden()) {
          return
        }
        pingFreshness()
          .then((response) => {
            handleSlotPickerPingResult(response)
          })
          .catch((err) => {
            logger.warn('Periodic clinical availability ping failed', { err })
            evaluateActiveStaleUsableExpiry()
          })
      }, SLOT_PICKER_PING_INTERVAL_MS)
    }

    /**
     * Slot picker opened: resolves true once the slots doc reflects a sync the server considers
     * fresh, false if superseded (picker left, consult type changed, newer visit), and rejects
     * after a bounded wait or repeated failures so the caller can show an error + Retry. Also
     * starts the ~3 min pings that keep the open picker fresh.
     */
    async function beginSlotPickerVisit(options?: { force?: boolean }): Promise<boolean> {
      const epoch = ++visitEpoch.value
      const type = consultType.value
      const isCurrent = () => visitEpoch.value === epoch && consultType.value === type
      requiredSuccessAtMs.value = null
      slotPickerVisitError.value = null
      clearActiveStaleUsableVisit()
      slotPickerVisitActive = true
      attachLifecycleListeners()
      startPeriodicPings()

      type SlotsDocWait = 'ready' | 'timeout' | 'cancelled'
      async function waitForSlotsDoc(requiredMs: number): Promise<SlotsDocWait> {
        requiredSuccessAtMs.value = requiredMs
        const caughtUp = await waitFor(
          () => isSlotsFreshForVisit.value,
          SLOTS_DOC_CATCH_UP_MS,
          epoch,
        )
        if (!isCurrent()) return 'cancelled'
        if (caughtUp) return 'ready'
        requiredSuccessAtMs.value = null
        return 'timeout'
      }

      const startedAt = monotonicNow()
      let failures = 0
      let force = options?.force ?? false
      let reusable = force ? null : rememberedFreshResponse(type)

      for (;;) {
        let response: ClinicalAvailabilityFreshness | null = reusable
        reusable = null
        if (!response) {
          const pingResult = await pingFreshnessWithinVisitBudget(startedAt, type, force)
          force = false
          if (!isCurrent()) return false
          if (pingResult.kind !== 'response') {
            failures++
            const elapsedMs = monotonicNow() - startedAt
            if (failures >= SLOT_PICKER_MAX_FAILURES || elapsedMs >= SLOT_PICKER_MAX_WAIT_MS) {
              throw new Error(SLOT_PICKER_UNAVAILABLE_MESSAGE)
            }
            if (pingResult.kind === 'error') {
              const remainingMs = SLOT_PICKER_MAX_WAIT_MS - elapsedMs
              const retryDelayMs = Math.min(DEFAULT_RETRY_DELAY_MS, remainingMs)
              if (retryDelayMs > 0) {
                await delay(retryDelayMs)
                if (!isCurrent()) return false
              }
            }
            continue
          }
          response = pingResult.response
        }

        let retryDelayMs = DEFAULT_RETRY_DELAY_MS
        let failureMessage = SLOT_PICKER_UNAVAILABLE_MESSAGE

        if (response && (response.status === 'fresh' || response.status === 'synced')) {
          if (response.lastSuccessAt) {
            const result = await waitForSlotsDoc(
              isoToMillis(response.lastSuccessAt, 'lastSuccessAt'),
            )
            if (result === 'cancelled') return false
            if (result === 'ready') {
              clearActiveStaleUsableVisit()
              return true
            }
            throw new Error(SLOT_PICKER_SLOW_MESSAGE)
          }
          failures++
        } else if (response?.status === 'in_flight') {
          const baseline = response.lastSuccessAt
            ? isoToMillis(response.lastSuccessAt, 'lastSuccessAt')
            : null
          const advanced = await waitFor(
            () => {
              const synced = syncStateLastSuccessAtMs.value
              return synced != null && (baseline == null || synced > baseline)
            },
            IN_FLIGHT_REPING_MS,
            epoch,
          )
          if (!isCurrent()) return false
          if (advanced && syncStateLastSuccessAtMs.value != null) {
            const result = await waitForSlotsDoc(syncStateLastSuccessAtMs.value)
            if (result === 'cancelled') return false
            if (result === 'ready') {
              clearActiveStaleUsableVisit()
              return true
            }
            throw new Error(SLOT_PICKER_SLOW_MESSAGE)
          }
          if (response && tryAcceptStaleUsableCache(response, type)) {
            return true
          }
          // Someone else's sync is slow; ask again rather than trusting the state doc alone.
          retryDelayMs = 0
          failureMessage = SLOT_PICKER_SLOW_MESSAGE
        } else if (response && (response.status === 'failed' || response.status === 'backoff')) {
          if (tryAcceptStaleUsableCache(response, type)) {
            return true
          }
          failures++
          retryDelayMs = Math.max(
            MIN_RETRY_DELAY_MS,
            response.retryAfterMs ?? DEFAULT_RETRY_DELAY_MS,
          )
        } else {
          failures++
          retryDelayMs = Math.max(
            MIN_RETRY_DELAY_MS,
            response?.retryAfterMs ?? DEFAULT_RETRY_DELAY_MS,
          )
        }

        const elapsedMs = monotonicNow() - startedAt
        if (
          failures >= SLOT_PICKER_MAX_FAILURES ||
          elapsedMs + retryDelayMs >= SLOT_PICKER_MAX_WAIT_MS
        ) {
          throw new Error(failureMessage)
        }
        if (retryDelayMs > 0) {
          await delay(retryDelayMs)
          if (!isCurrent()) return false
        }
      }
    }

    /** Slot picker left (or session reset): cancel the visit and stop the periodic pings. */
    function endSlotPickerVisit() {
      visitEpoch.value++
      slotPickerVisitActive = false
      stopPeriodicPings()
      requiredSuccessAtMs.value = null
      slotPickerVisitError.value = null
      clearActiveStaleUsableVisit()
      clearSessionHiddenSlots()
      maybeDetachLifecycleListeners()
    }

    watch(
      () => tellescopeAvailability.value?.availabilityBlocks,
      () => {
        pruneSessionHiddenSlots()
      },
    )

    const userIdToConsultantId = computed(() => externalUserIdToConsultantId.value)

    watch(consultType, (next, prev) => {
      if (next === prev || (!isSubscribed.value && !subscribeInFlight)) {
        return
      }
      logger.info('consultType changed — switching availability subscription', {
        from: prev,
        to: next,
      })
      try {
        subscribeAvailabilityDoc()
      } catch (err) {
        logger.error('Error switching clinical availability subscription', { err })
      }
    })

    watch(
      () => reservationExpiryTick.value,
      () => {
        evaluateActiveStaleUsableExpiry()
      },
    )

    watch(updatedAt, (slotsUpdatedAt) => {
      if (slotsUpdatedAt == null) {
        return
      }
      clearStaleUsableWhenSlotsDocAdvances(slotsUpdatedAt.getTime())
    })

    watch([everyFiveMinutes], async () => {
      const local = activeDraftSelect.value
      if (!local) {
        return
      }
      const entry = clinicalCustomerDraftSelects.value[local.customerId]
      if (!entry || !localDraftSelectMatchesEntry(local, entry)) {
        activeDraftSelect.value = null
        return
      }
      try {
        await makeSlotAsSelected(local)
      } catch (err) {
        logger.error('Failed to refresh active draft select', { err })
      }
    })

    return {
      consultType,
      /** @deprecated Use filterStateCode */
      stateCode: filterStateCode,
      filterStateCode,
      filterTimezone,
      availabilitySlots,
      updatedAt,
      isSlotsFreshForVisit,
      slotPickerVisitError,
      isSubscribed,
      clinicalReservations,
      clinicalCustomerDraftSelects,
      userIdToConsultantId,
      makeSlotAsSelected,
      unmarkSlotAsSelected,
      hideSlotForSession,
      sessionHiddenSlotKeys,
      setConsultType,
      setStateCode,
      setFilterStateCode,
      setLicensedStateMaps,
      signOut,
      forceResubscribe,
      getCustomerDraftSelect,
      pingFreshness,
      beginSlotPickerVisit,
      endSlotPickerVisit,
      subscribe,
    }
  },
)
